/**
 * Promote a StatementFlow bank import into a real LE case:
 * create/attach case → Evidence Locker row → persist account+CP edges → open Case Canvas.
 */
import { caseService } from '../services/api';
import { persistCaseEdge } from '../services/caseCanvas';
import { normalizeIdentifier } from '../case-canvas/entityJoin';
import type { BankImportDossier } from '../stores/useBankImportStore';

function accountCanon(accountNodeId: string, accountNumber?: string | null): string {
  if (accountNumber?.trim()) {
    return normalizeIdentifier('account', accountNumber.trim()) || accountNumber.trim();
  }
  const raw = accountNodeId.replace(/^ent:account:/, '').replace(/^sf:acct:/, '').replace(/^sf:account:/, '');
  return normalizeIdentifier('account', raw) || raw;
}

/** Persist subject ↔ account and account ↔ top counterparties onto the case graph. */
export function persistBankDossierEdges(leCaseId: string, dossier: BankImportDossier): void {
  const holder = (dossier.kyc?.holder_name || '').trim() || 'Subject';
  const acctLabel = holder
    || dossier.kyc?.account_number
    || dossier.fileName
    || 'Imported account';
  const acctCanon = accountCanon(dossier.accountNodeId, dossier.kyc?.account_number);

  persistCaseEdge(leCaseId, {
    source: { module: 'shared', type: 'identity_hub', canonical_id: 'case', label: holder },
    target: { module: 'bank', type: 'account', canonical_id: acctCanon, label: acctLabel },
    edge_type: 'cross_link',
    determinism: 'deterministic',
    attribution: {
      source: 'fusion',
      method: 'bank_import_promote',
      statement_id: dossier.statementId,
      sf_case_id: dossier.sfCaseId,
    },
  });

  for (const cp of dossier.counterparties.slice(0, 40)) {
    const name = (cp.name || '').trim();
    if (!name) continue;
    const cpCanon = normalizeIdentifier('person', name) || name.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 40);
    if (cp.debit > 0) {
      persistCaseEdge(leCaseId, {
        source: { module: 'bank', type: 'account', canonical_id: acctCanon, label: acctLabel },
        target: { module: 'bank', type: 'person', canonical_id: cpCanon, label: name },
        edge_type: 'trace',
        determinism: 'deterministic',
        value: cp.debit,
        attribution: { source: 'fusion', method: 'bank_import_debit', counterparty: name },
      });
    }
    if (cp.credit > 0) {
      persistCaseEdge(leCaseId, {
        source: { module: 'bank', type: 'person', canonical_id: cpCanon, label: name },
        target: { module: 'bank', type: 'account', canonical_id: acctCanon, label: acctLabel },
        edge_type: 'trace',
        determinism: 'deterministic',
        value: cp.credit,
        attribution: { source: 'fusion', method: 'bank_import_credit', counterparty: name },
      });
    }
    if (!cp.debit && !cp.credit) {
      persistCaseEdge(leCaseId, {
        source: { module: 'bank', type: 'account', canonical_id: acctCanon, label: acctLabel },
        target: { module: 'bank', type: 'person', canonical_id: cpCanon, label: name },
        edge_type: 'trace',
        determinism: 'deterministic',
        attribution: { source: 'fusion', method: 'bank_import_counterparty', counterparty: name },
      });
    }
  }
}

export function dossierToEvidencePayload(dossier: BankImportDossier): Record<string, unknown> {
  return {
    kind: 'bank_statement',
    holder_name: dossier.kyc?.holder_name || null,
    account_number: dossier.kyc?.account_number || null,
    ifsc: dossier.kyc?.ifsc || null,
    branch: dossier.kyc?.branch || null,
    bank: dossier.kyc?.bank || null,
    bank_location: dossier.kyc?.bank_location || null,
    transaction_count: dossier.summary.txns,
    counterparty_count: dossier.summary.cps,
    debit_total: dossier.summary.dr,
    credit_total: dossier.summary.cr,
    narrative_summary: dossier.statement?.narrative_summary || null,
    risk_level: dossier.statement?.risk_level || null,
    anomalies: dossier.statement?.anomalies || [],
    statement_id: dossier.statementId,
    sf_case_id: dossier.sfCaseId,
    account_id: dossier.accountId,
    account_node_id: dossier.accountNodeId,
    source_file_name: dossier.fileName || dossier.statement?.source_file_name || null,
    deep_link: dossier.deepLink || null,
    imported_at: dossier.importedAt,
    // Cap payload size for Evidence Locker; full ledger stays in StatementFlow.
    transactions: dossier.transactions.slice(0, 200),
    counterparties: dossier.counterparties.slice(0, 40),
  };
}

export async function saveBankDossierAsEvidence(
  leCaseId: string,
  dossier: BankImportDossier,
): Promise<void> {
  const holder = (dossier.kyc?.holder_name || '').trim();
  const acct = (dossier.kyc?.account_number || '').trim();
  const title = holder
    ? `Bank statement — ${holder}${acct ? ` (${acct})` : ''}`
    : `Bank statement — ${dossier.fileName || dossier.statementId}`;
  const description = [
    `${dossier.summary.txns} transactions`,
    `${dossier.summary.cps} counterparties`,
    `Debits ₹${Math.round(dossier.summary.dr).toLocaleString('en-IN')}`,
    `Credits ₹${Math.round(dossier.summary.cr).toLocaleString('en-IN')}`,
  ].join(' · ');

  await caseService.addEvidence(leCaseId, {
    evidence_type: 'bank_statement',
    title,
    description,
    source: 'StatementFlow',
    source_ref: dossier.statementId,
    payload: dossierToEvidencePayload(dossier),
  });
}

export async function promoteBankImportToCase(opts: {
  dossier: BankImportDossier;
  mode: 'new' | 'existing';
  caseTitle?: string;
  firNumber?: string;
  existingCaseId?: string;
}): Promise<{ caseId: string }> {
  let caseId = opts.existingCaseId || '';
  if (opts.mode === 'new') {
    const holder = (opts.dossier.kyc?.holder_name || '').trim();
    const title = (opts.caseTitle || '').trim()
      || (holder ? `Bank statement — ${holder}` : 'Bank statement import');
    const created = await caseService.create({
      title,
      fir_number: opts.firNumber?.trim() || undefined,
      notes: opts.dossier.statement?.narrative_summary || undefined,
    });
    caseId = String(created?.id || created?.case_id || '');
    if (!caseId) throw new Error('Case create returned no id');
  } else if (!caseId) {
    throw new Error('Pick an existing case');
  }

  await saveBankDossierAsEvidence(caseId, opts.dossier);
  persistBankDossierEdges(caseId, opts.dossier);
  return { caseId };
}
